/* eslint-disable angular/timeout-service */
/* eslint-disable angular/module-getter */
/* eslint-disable max-classes-per-file */

// Deps
import { element } from 'angular';

// @ts-ignore
import ts from 'highlight.js/lib/languages/typescript';
import 'highlight.js/styles/github-dark.css';

import Utils from '~/scripts/services/utils';

import '~/components/file-viewer/file-viewer.module';

import { createEditor, KnitivEditor, Plugins } from '@knitiv/text-editor';
import { BaseController } from '~/utils/base-controller';
import showImportDocumentModal from './modals/insert-document/insert-document.component';
import showImportNodeModal from './modals/insert-node/insert-node.component';
import { fromBlobToFileUpload } from './utils';
import { NgNodeViewRenderer } from './text-editor.renderer';

/* globals Knitiv */

// TODO realtime co-editing https://github.com/yjs/y-prosemirror
// TODO resize image https://gitlabjf.ccomptes.fr/EIG_2019/plume/plume-prosemirror/-/merge_requests/102/diffs

// If config needed
const Headings = Plugins.Heading;
export default class extends BaseController {
	/** @type {string} */
	value;

	/** @type {?KnitivEditor} */
	editor;

	/** @type {import('angular').IControllerConstructor} */
	/* @ngInject */
	constructor(
		/** @type {Knitiv.Scope} */
		$scope,
		/** @type {Knitiv.Angular.Element} */
		$element,
		/** @type {Knitiv.Angular.Injector} */
		$injector,
	) {
		super($injector);
		this.$scope = $scope;
		this.$element = $element;
		this.$injector = $injector;

		this.editor = null;

		/** @type {boolean} */
		this.disabled = false;

		/** @type {?import('angular').INgModelController} */
		this.ngModelCtrl = null;

		/** @type {?Function} */
		this.onValueChanged = null;

		/** @type {boolean} */
		this.ready = false;

		/** @type {boolean} */
		this.required = false;
		this.value = '';

		/** @type {boolean} */
		this.hideToolbar = false;

		/** @type {boolean} */
		this.readonly = false;

		/** @type {boolean} */
		this.isDirty = false;
	}

	isEditable() {
		return !this.disabled && !this.readonly;
	}

	toggleBold() {
		this.editor?.toggleBold();
	}

	toggleItalic() {
		this.editor?.toggleItalic();
	}

	toggleUnderline() {
		this.editor?.toggleUnderline();
	}

	toggleStrike() {
		this.editor?.toggleStrike();
	}

	alignLeft() {
		this.editor?.alignLeft();
	}

	alignCenter() {
		this.editor?.alignCenter();
	}

	alignRight() {
		this.editor?.alignRight();
	}

	alignJustify() {
		this.editor?.alignJustify();
	}

	toggleCode() {
		this.editor?.toggleCode();
	}

	toggleCodeBlock() {
		this.editor?.toggleCodeBlock();
	}

	toggleLink() {
		this.editor?.toggleLink({ href: this.editor?.getSelectedText() ?? '' });
	}

	insertTable() {
		this.editor?.insertTable();
	}

	toggleQuote() {
		this.editor?.toggleBlockquote();
	}

	toggleBulletList() {
		this.editor?.toggleBulletList();
	}

	sinkListItem() {
		this.editor?.commands.sinkListItem(Plugins.ListItem.name);
	}

	liftListItem() {
		this.editor?.commands.liftListItem(Plugins.ListItem.name);
	}

	toggleOrderedList() {
		this.editor?.toggleOrderedList();
	}

	isHeading() {
		return this.editor?.isActive(Plugins.Heading.name);
	}

	getHeading() {
		return this.editor?.getAttributes(Plugins.Heading.name);
	}

	getHeadingOptions() {
		return Headings.options.levels;
	}

	setParagraph() {
		this.editor?.chain().setParagraph().focus().run();
	}

	isParagraph() {
		return this.editor?.isActive(Plugins.Paragraph.name);
	}

	/**
	 * @param {Plugins['Heading']['options']['levels'][number]} level
	 */
	setHeading(level) {
		this.editor?.chain().setHeading({ level }).focus().run();
	}

	/**
	 *
	 * @param {import('~/scripts/services/model/file-upload').default} fileUpload
	 * @returns {boolean}
	 */
	insertDocument(fileUpload) {
		this.editor?.commands.insertContent({
			type: Plugins.KDocument.name,
			attrs: {
				kid: fileUpload.kid,
			},
		});
		return this.editor?.commands.focus() ?? false;
	}

	insertFile() {
		this.$uibModal
			.open(showImportDocumentModal).result
			.then(fileUpload => this.insertDocument(fileUpload))
			.catch(error => {
				console.error('error', error);
			});
	}

	/**
	 * @param {import('~/scripts/services/model/node').default} node
	 * @returns {*|boolean}
	 */
	insertNode(node) {
		this.editor?.commands.insertContent({
			type: Plugins.KNode.name,
			attrs: {
				kid: node.kid,
			},
		});
		return this.editor?.commands.focus() ?? false;
	}

	async searchNode() {
		const node = await this.$uibModal.open(showImportNodeModal).result;
		if (node) {
			this.insertNode(node);
		}
	}

	/** @param {import('angular').IOnChangesObject} changes */
	$onChanges(changes) {
		if (!this.editor) {
			return;
		}

		if (changes.value && this.ready /* && this.value !== changes.value.currentValue */) {
			const currentValue = this.editor?.getHTML();

			if (changes.value.currentValue !== currentValue) {
				this.value = changes.value.currentValue;
				// do not emit update & preserve full withespace (otherwise space doesn't work)
				this.editor?.commands.setContent(this.value, false);
			}
		}

		if (
			(changes.disabled || changes.readonly) && this.editor) {
			// this.$element.find('.ProseMirror').attr('contenteditable', !this.disabled);
			this.editor?.setEditable(this.isEditable());
		}
	}

	async $onInit() {
		this.editor = createEditor({
			extensions: [
				Plugins.Document,
				Plugins.Paragraph,
				Plugins.Text,
				Plugins.Bold,
				Plugins.Links,
				Plugins.BlockQuote,
				Plugins.Table,
				Plugins.TableCell,
				Plugins.TableHeader,
				Plugins.TableRow,
				Plugins.Italic,
				Plugins.Underline,
				Plugins.Align,
				Plugins.Code,
				Plugins.CodeBlock.configure({
					HTMLAttributes: {
						spellcheck: 'false',
						class: 'hljs',
					},
				}),
				Plugins.Strike,
				Plugins.Dropcursor,
				Plugins.Gapcursor,
				Headings,
				Plugins.KNode.configure({
					renderer: NgNodeViewRenderer('text-editor-node'),
				}),
				Plugins.KDocument.configure({
					renderer: NgNodeViewRenderer('file-viewer'),
					handlePaste: (/** @type {any} */ item) => {
						if (item.kind === 'string') {
							return false;
						}
						// TODO: add placeholder
						const blob = item.getAsFile();
						if (!blob) {
							return false;
						}
						fromBlobToFileUpload(this.$injector, blob, 'K_NODE;DOCEMBEDDED')
							.then(fileUpload => {
								if (!fileUpload) {
									return false;
								}
								return this.insertDocument(fileUpload);
							})
							.catch(error => {
								console.error('e', error);
							});

						return true;
					},
				}),
				Plugins.History,
				Plugins.Typography,
				Plugins.TaskItem,
				Plugins.TaskList,
				Plugins.ListItem,
				Plugins.OrderedList,
				Plugins.BulletList,
			],
			editable: this.isEditable(),
			content: this.value,
			/**
			 * @param {any} editor
			 */
			onUpdate: ({ editor }) => {
				this.$timeout(() => {
					if (editor.isEmpty && !this.isDirty) {
						return;
					}

					this.value = editor.getHTML();
					this.ngModelCtrl?.$setViewValue(this.value);
					if (typeof this.onValueChanged === 'function') {
						this.onValueChanged({ $event: this.value });
					}
					this.ngModelCtrl?.$validate();

					this.isDirty = true;
				});
			},
		});

		this.editor?.registerLanguage('ts', ts);

		// const { view, onContentChanged, setContent } = editor;

		// onContentChanged(content => {
		// 	console.log('content changed', content);
		// });

		// console.log('view', view);

		// if (this.value) {
		// 	this.editor?.commands.setContent(this.value, false);
		// }

		// Load editor with value on first change
		element(document)
			.ready(() => {
				const editorSelector = this.$element.find('.rich-editor__editor');

				const editorElement = editorSelector.get(0);
				if (this.editor && editorElement) {
					editorElement.append(this.editor.view.dom);
				}
				this.ready = true;
			});

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$validators.required = (/** @type {string} */ modelValue) => {
				let valid = true;

				if (this.required && Utils.stripTags(modelValue).trim() === '') {
					valid = false;
				}

				return valid;
			};
		}
	}
}
